import * as BABYLON from "@babylonjs/core";
import "@babylonjs/core/Layers/layer";
import "@babylonjs/loaders/glTF";

export type GpuTier = "low" | "high";

export interface WorldVisualAssets {
  skinlandsBackdropPath: string;
  runtimeGlbs?: {
    ashBoundSmith?: string;
    weaponGreatsword?: string;
    remnantChassis?: string;
    remnantHead?: string;
    remnantLimb?: string;
  };
  runtimeVisuals?: {
    ashBoundSmithCutout?: string;
  };
}

export function detectGPUTier(): GpuTier {
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl");
  const debug = gl?.getExtension("WEBGL_debug_renderer_info");
  const renderer = debug ? gl?.getParameter(debug.UNMASKED_RENDERER_WEBGL) as string : "";
  if (/iris 6100|intel/i.test(renderer)) return "low";
  return navigator.hardwareConcurrency <= 4 ? "low" : "high";
}

export function createEngine(canvas: HTMLCanvasElement) {
  const engine = new BABYLON.Engine(canvas, true, {
    preserveDrawingBuffer: false,
    stencil: false,
    antialias: false
  });
  const scene = new BABYLON.Scene(engine);
  scene.clearColor = new BABYLON.Color4(0.039, 0.039, 0.059, 1);
  const gpuTier = detectGPUTier();

  if (gpuTier === "low") {
    engine.setHardwareScalingLevel(1.35);
    scene.fogMode = BABYLON.Scene.FOGMODE_EXP2;
    scene.fogDensity = 0.02;
  }

  return { engine, scene, gpuTier };
}

const TITLE_GLYPHS: Record<string, string[]> = {
  S: ["1111", "1000", "1000", "1110", "0001", "0001", "1110"],
  O: ["1110", "1001", "1001", "1001", "1001", "1001", "1110"],
  U: ["1001", "1001", "1001", "1001", "1001", "1001", "1110"],
  L: ["1000", "1000", "1000", "1000", "1000", "1000", "1111"],
  M: ["10001", "11011", "10101", "10101", "10001", "10001", "10001"],
  I: ["111", "010", "010", "010", "010", "010", "111"],
  T: ["11111", "00100", "00100", "00100", "00100", "00100", "00100"],
  H: ["1001", "1001", "1001", "1111", "1001", "1001", "1001"]
};

function buildExtrudedTitle(scene: BABYLON.Scene) {
  const root = new BABYLON.TransformNode("soulsmith-title-root", scene);
  root.position = new BABYLON.Vector3(0, 1.82, 0.34);
  root.rotation.x = -0.12;

  const gold = new BABYLON.StandardMaterial("soulsmith-title-gold", scene);
  gold.diffuseColor = BABYLON.Color3.FromHexString("#d4af37");
  gold.emissiveColor = new BABYLON.Color3(0.18, 0.12, 0.02);
  gold.specularColor = BABYLON.Color3.FromHexString("#ffffff");

  const bloodShadow = new BABYLON.StandardMaterial("soulsmith-title-blood-shadow", scene);
  bloodShadow.diffuseColor = BABYLON.Color3.FromHexString("#2b0707");
  bloodShadow.emissiveColor = BABYLON.Color3.FromHexString("#8b0000").scale(0.18);

  const word = "SOULSMITH";
  const cell = 0.105;
  const gap = 0.055;
  const letterGap = 0.115;
  const depth = 0.16;
  const totalColumns = word.split("").reduce((sum, letter) => sum + TITLE_GLYPHS[letter][0].length, 0) + (word.length - 1) * (letterGap / (cell + gap));
  const totalWidth = totalColumns * (cell + gap);
  let cursor = -totalWidth / 2;
  let blockCount = 0;

  for (const letter of word) {
    const glyph = TITLE_GLYPHS[letter];
    const columns = glyph[0].length;
    for (let row = 0; row < glyph.length; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        if (glyph[row][column] !== "1") continue;
        const x = cursor + column * (cell + gap);
        const y = (glyph.length - row) * (cell + gap);
        const shadow = BABYLON.MeshBuilder.CreateBox(`soulsmith-title-shadow-${blockCount}`, { width: cell, height: cell, depth: depth }, scene);
        shadow.position = new BABYLON.Vector3(x + 0.024, y - 0.024, -0.11);
        shadow.parent = root;
        shadow.material = bloodShadow;

        const block = BABYLON.MeshBuilder.CreateBox(`soulsmith-title-block-${blockCount}`, { width: cell, height: cell, depth }, scene);
        block.position = new BABYLON.Vector3(x, y, 0);
        block.parent = root;
        block.material = gold;
        blockCount += 1;
      }
    }
    cursor += columns * (cell + gap) + letterGap;
  }

  return { root, blockCount };
}

export function buildLandingWeapon(scene: BABYLON.Scene) {
  const camera = new BABYLON.ArcRotateCamera("crucible-camera", -Math.PI / 2, Math.PI / 2.5, 7, BABYLON.Vector3.Zero(), scene);
  camera.attachControl(scene.getEngine().getRenderingCanvas(), true);
  camera.lowerRadiusLimit = 4;
  camera.upperRadiusLimit = 13;
  const title = buildExtrudedTitle(scene);

  const frameCamera = () => {
    const engine = scene.getEngine();
    const aspect = engine.getRenderWidth() / Math.max(engine.getRenderHeight(), 1);
    const narrow = aspect < 0.7;
    camera.radius = narrow ? 11 : 7;
    camera.setTarget(new BABYLON.Vector3(0, narrow ? -0.15 : 0.05, 0));
    title.root.position.y = narrow ? 1.75 : 1.82;
    title.root.scaling.setAll(narrow ? 0.34 : 0.78);
  };
  frameCamera();
  scene.getEngine().onResizeObservable.add(frameCamera);

  const light = new BABYLON.PointLight("blood-forge-light", new BABYLON.Vector3(0, 4, -3), scene);
  light.diffuse = new BABYLON.Color3(0.55, 0, 0);
  light.intensity = 70;

  const titleLight = new BABYLON.PointLight("title-ember-light", new BABYLON.Vector3(0, 3.2, -1.2), scene);
  titleLight.diffuse = BABYLON.Color3.FromHexString("#d4af37");
  titleLight.intensity = 22;

  const gold = new BABYLON.StandardMaterial("worn-gold", scene);
  gold.diffuseColor = BABYLON.Color3.FromHexString("#d4af37");
  gold.emissiveColor = new BABYLON.Color3(0.22, 0.14, 0.02);

  const blood = new BABYLON.StandardMaterial("blood-crack", scene);
  blood.diffuseColor = BABYLON.Color3.FromHexString("#8b0000");
  blood.emissiveColor = BABYLON.Color3.FromHexString("#8b0000");

  const blade = BABYLON.MeshBuilder.CreateBox("mythic-blade", { width: 0.22, height: 4.4, depth: 0.12 }, scene);
  blade.rotation.z = Math.PI / 4;
  blade.material = gold;

  const guard = BABYLON.MeshBuilder.CreateBox("fractured-guard", { width: 2.1, height: 0.18, depth: 0.2 }, scene);
  guard.rotation.z = Math.PI / 4;
  guard.position.y = -1.3;
  guard.material = blood;

  const hilt = BABYLON.MeshBuilder.CreateCylinder("black-hilt", { height: 1.6, diameter: 0.22, tessellation: 6 }, scene);
  hilt.position.y = -2.1;
  hilt.rotation.z = Math.PI / 4;
  hilt.material = blood;

  const particles = new BABYLON.ParticleSystem("ash", 480, scene);
  particles.particleTexture = new BABYLON.Texture("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+/p94AAAAASUVORK5CYII=", scene);
  particles.emitter = BABYLON.Vector3.Zero();
  particles.minEmitBox = new BABYLON.Vector3(-4, -3, -2);
  particles.maxEmitBox = new BABYLON.Vector3(4, 3, 2);
  particles.color1 = new BABYLON.Color4(0.48, 0.48, 0.48, 1);
  particles.color2 = new BABYLON.Color4(0.55, 0, 0, 1);
  particles.minSize = 0.02;
  particles.maxSize = 0.08;
  particles.emitRate = 80;
  particles.gravity = new BABYLON.Vector3(0, -0.02, 0);
  particles.start();

  scene.onBeforeRenderObservable.add(() => {
    const dt = scene.getEngine().getDeltaTime() / 1000;
    blade.rotation.y += dt * 0.45;
    guard.rotation.y += dt * 0.45;
    hilt.rotation.y += dt * 0.45;
  });

  return { camera, blade, guard, hilt, lights: [light, titleLight], titleRoot: title.root, titleBlockCount: title.blockCount };
}

interface WorldAuditState {
  active: boolean;
  biome: "Skinlands";
  cameraViewDegrees: number;
  playerVisible: boolean;
  forgedWeaponVisible: boolean;
  echoGhostVisible: boolean;
  anvilLandmarkVisible: boolean;
  readableEnemyCount: number;
  terrainDetailCount: number;
  materialLayerCount: number;
  particleBudget: number;
  qualityGateReady: boolean;
}

function standardMaterial(scene: BABYLON.Scene, name: string, diffuse: string, emissive = "#000000", alpha = 1) {
  const material = new BABYLON.StandardMaterial(name, scene);
  material.diffuseColor = BABYLON.Color3.FromHexString(diffuse);
  material.emissiveColor = BABYLON.Color3.FromHexString(emissive);
  material.specularColor = BABYLON.Color3.FromHexString("#111111");
  material.alpha = alpha;
  if (alpha < 1) material.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND;
  return material;
}

function createSkinlandsCorpseTerrainMaterial(scene: BABYLON.Scene) {
  const material = standardMaterial(scene, "skinlands-layered-dead-skin-material", "#ffffff", "#050000") as BABYLON.StandardMaterial;
  const diffuse = new BABYLON.DynamicTexture("skinlands-dead-god-skin-diffuse-mask", { width: 1024, height: 1024 }, scene, false);
  const emissive = new BABYLON.DynamicTexture("skinlands-red-black-fissure-emissive-mask", { width: 1024, height: 1024 }, scene, false);
  const diffuseContext = diffuse.getContext() as unknown as CanvasRenderingContext2D;
  const emissiveContext = emissive.getContext() as unknown as CanvasRenderingContext2D;

  diffuseContext.fillStyle = "#82574f";
  diffuseContext.fillRect(0, 0, 1024, 1024);
  emissiveContext.fillStyle = "#120504";
  emissiveContext.fillRect(0, 0, 1024, 1024);

  for (let index = 0; index < 72; index += 1) {
    const x = 24 + ((index * 137) % 976);
    const y = 18 + ((index * 211) % 988);
    const radiusX = 28 + ((index * 19) % 92);
    const radiusY = 12 + ((index * 31) % 58);
    diffuseContext.save();
    diffuseContext.translate(x, y);
    diffuseContext.rotate(Math.sin(index * 1.73) * 1.8);
    diffuseContext.fillStyle = index % 3 === 0 ? "rgba(125, 61, 66, 0.56)" : "rgba(97, 57, 51, 0.42)";
    diffuseContext.beginPath();
    diffuseContext.ellipse(0, 0, radiusX, radiusY, 0, 0, Math.PI * 2);
    diffuseContext.fill();
    diffuseContext.restore();
  }

  for (let index = 0; index < 46; index += 1) {
    const startX = ((index * 83) % 980) + 20;
    const startY = ((index * 127) % 980) + 22;
    diffuseContext.strokeStyle = index % 2 === 0 ? "rgba(27, 8, 8, 0.68)" : "rgba(155, 128, 104, 0.36)";
    diffuseContext.lineWidth = 1 + (index % 3);
    diffuseContext.beginPath();
    diffuseContext.moveTo(startX, startY);
    for (let step = 1; step <= 6; step += 1) {
      diffuseContext.lineTo(startX + Math.sin((index + step) * 1.47) * 54 + step * 28, startY + Math.cos((index - step) * 1.19) * 28 + step * 19);
    }
    diffuseContext.stroke();
  }

  const fissurePaths = [
    [[60, 742], [158, 705], [252, 738], [370, 690], [474, 724]],
    [[226, 190], [320, 250], [432, 220], [548, 292], [676, 260]],
    [[505, 850], [562, 730], [642, 682], [706, 574], [802, 510], [940, 462]],
    [[96, 430], [198, 392], [318, 418], [440, 370], [584, 382]],
    [[724, 124], [690, 222], [738, 310], [702, 416], [760, 520]],
    [[332, 580], [438, 546], [552, 574], [648, 538], [786, 586], [922, 550]]
  ];

  for (let pathIndex = 0; pathIndex < fissurePaths.length; pathIndex += 1) {
    const path = fissurePaths[pathIndex];
    diffuseContext.lineCap = "round";
    diffuseContext.lineJoin = "round";
    emissiveContext.lineCap = "round";
    emissiveContext.lineJoin = "round";
    diffuseContext.strokeStyle = "rgba(7, 0, 0, 0.86)";
    diffuseContext.lineWidth = 12 - Math.min(pathIndex, 4);
    diffuseContext.beginPath();
    emissiveContext.strokeStyle = "rgba(94, 6, 6, 0.82)";
    emissiveContext.lineWidth = 4;
    emissiveContext.beginPath();

    for (let pointIndex = 0; pointIndex < path.length; pointIndex += 1) {
      const [baseX, baseY] = path[pointIndex];
      const jagX = baseX + Math.sin((pointIndex + pathIndex) * 1.91) * 18;
      const jagY = baseY + Math.cos((pointIndex - pathIndex) * 1.37) * 16;
      if (pointIndex === 0) {
        diffuseContext.moveTo(jagX, jagY);
        emissiveContext.moveTo(jagX, jagY);
      } else {
        diffuseContext.lineTo(jagX, jagY);
        emissiveContext.lineTo(jagX, jagY);
      }
    }

    diffuseContext.stroke();
    emissiveContext.stroke();

    diffuseContext.strokeStyle = "rgba(108, 7, 6, 0.68)";
    diffuseContext.lineWidth = 3;
    diffuseContext.stroke();
  }

  for (let index = 0; index < 34; index += 1) {
    const x = 44 + ((index * 233) % 938);
    const y = 38 + ((index * 181) % 942);
    const length = 46 + ((index * 17) % 88);
    const angle = Math.sin(index * 0.91) * Math.PI;
    diffuseContext.save();
    diffuseContext.translate(x, y);
    diffuseContext.rotate(angle);
    diffuseContext.strokeStyle = index % 4 === 0 ? "rgba(139, 128, 104, 0.38)" : "rgba(54, 18, 16, 0.46)";
    diffuseContext.lineWidth = index % 4 === 0 ? 5 : 3;
    diffuseContext.beginPath();
    diffuseContext.moveTo(-length * 0.5, 0);
    diffuseContext.quadraticCurveTo(0, Math.sin(index) * 18, length * 0.5, 0);
    diffuseContext.stroke();
    diffuseContext.restore();
  }

  for (let index = 0; index < 260; index += 1) {
    const x = (index * 61) % 1024;
    const y = (index * 157) % 1024;
    const shade = 64 + (index % 58);
    diffuseContext.fillStyle = `rgba(${shade}, ${Math.max(30, shade - 20)}, ${Math.max(26, shade - 28)}, 0.32)`;
    diffuseContext.fillRect(x, y, 1 + (index % 3), 1 + ((index + 1) % 3));
  }

  diffuse.update(false);
  emissive.update(false);
  diffuse.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
  diffuse.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
  emissive.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
  emissive.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
  material.diffuseTexture = diffuse;
  material.diffuseColor = BABYLON.Color3.FromHexString("#ffffff");
  material.emissiveColor = BABYLON.Color3.FromHexString("#220b0a");
  material.specularColor = BABYLON.Color3.FromHexString("#090606");
  material.disableLighting = true;
  material.backFaceCulling = false;
  material.alpha = 0.52;
  material.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND;
  return material;
}

function createSkinlandsSkylineTexture(scene: BABYLON.Scene, sourceKey: string) {
  const texture = new BABYLON.DynamicTexture("skinlands-baked-far-lod-skyline-texture", { width: 1280, height: 720 }, scene, false);
  const context = texture.getContext() as unknown as CanvasRenderingContext2D;
  const seedOffset = sourceKey.length % 47;

  const sky = context.createLinearGradient(0, 0, 0, 720);
  sky.addColorStop(0, "#050609");
  sky.addColorStop(0.42, "#101014");
  sky.addColorStop(0.72, "#17100f");
  sky.addColorStop(1, "#0a0505");
  context.fillStyle = sky;
  context.fillRect(0, 0, 1280, 720);

  for (let index = 0; index < 38; index += 1) {
    const x = ((index * 173 + seedOffset * 19) % 1320) - 20;
    const y = 62 + ((index * 59) % 260);
    const radius = 72 + ((index * 31) % 140);
    context.fillStyle = index % 2 === 0 ? "rgba(92, 84, 78, 0.12)" : "rgba(105, 61, 54, 0.08)";
    context.beginPath();
    context.ellipse(x, y, radius * 1.6, radius * 0.42, Math.sin(index) * 0.25, 0, Math.PI * 2);
    context.fill();
  }

  const drawSpire = (x: number, base: number, height: number, width: number, color: string, glow = false) => {
    context.fillStyle = color;
    context.beginPath();
    context.moveTo(x - width * 0.5, base);
    context.lineTo(x - width * 0.18, base - height * 0.72);
    context.lineTo(x, base - height);
    context.lineTo(x + width * 0.22, base - height * 0.68);
    context.lineTo(x + width * 0.5, base);
    context.closePath();
    context.fill();
    if (glow) {
      context.strokeStyle = "rgba(139, 0, 0, 0.52)";
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(x + width * 0.12, base - height * 0.14);
      context.lineTo(x + width * 0.04, base - height * 0.55);
      context.lineTo(x + width * 0.16, base - height * 0.82);
      context.stroke();
    }
  };

  for (let index = 0; index < 16; index += 1) {
    const x = 38 + index * 83 + Math.sin(index * 1.4) * 24;
    const height = 150 + ((index * 37) % 250);
    drawSpire(x, 494 + Math.sin(index) * 20, height, 28 + (index % 4) * 14, index % 3 === 0 ? "#030306" : "#090a0d", index % 4 === 0);
  }

  context.fillStyle = "rgba(23, 15, 14, 0.96)";
  context.beginPath();
  context.moveTo(0, 514);
  for (let x = 0; x <= 1280; x += 80) {
    context.lineTo(x, 498 + Math.sin(x * 0.012 + seedOffset) * 22 + Math.cos(x * 0.019) * 14);
  }
  context.lineTo(1280, 720);
  context.lineTo(0, 720);
  context.closePath();
  context.fill();

  context.strokeStyle = "rgba(116, 105, 86, 0.12)";
  context.lineWidth = 4;
  for (let index = 0; index < 8; index += 1) {
    const x = 40 + index * 170;
    const y = 560 + Math.sin(index * 0.8) * 24;
    context.beginPath();
    context.arc(x, y, 72 + (index % 3) * 18, Math.PI * 1.08, Math.PI * 1.82);
    context.stroke();
  }

  context.strokeStyle = "rgba(139, 0, 0, 0.18)";
  context.lineWidth = 1;
  for (let index = 0; index < 42; index += 1) {
    const x = (index * 71 + seedOffset * 13) % 1280;
    const y = 360 + ((index * 47) % 240);
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + Math.sin(index * 1.8) * 34, y + 18 + Math.cos(index) * 10);
    context.stroke();
  }

  const eclipseX = 972;
  const eclipseY = 126;
  const eclipseGlow = context.createRadialGradient(eclipseX, eclipseY, 14, eclipseX, eclipseY, 92);
  eclipseGlow.addColorStop(0, "rgba(139, 0, 0, 0.62)");
  eclipseGlow.addColorStop(0.36, "rgba(139, 0, 0, 0.24)");
  eclipseGlow.addColorStop(1, "rgba(139, 0, 0, 0)");
  context.fillStyle = eclipseGlow;
  context.beginPath();
  context.arc(eclipseX, eclipseY, 92, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#120607";
  context.beginPath();
  context.arc(eclipseX, eclipseY, 29, 0, Math.PI * 2);
  context.fill();
  context.strokeStyle = "rgba(240, 210, 180, 0.62)";
  context.lineWidth = 2;
  context.stroke();

  context.strokeStyle = "rgba(139, 0, 0, 0.56)";
  context.lineWidth = 2;
  for (let index = 0; index < 26; index += 1) {
    const x = (index * 97 + seedOffset * 11) % 1280;
    const y = 70 + ((index * 43) % 520);
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + Math.sin(index) * 22, y + 1 + (index % 3));
    context.stroke();
  }

  texture.update(false);
  return texture;
}

function createSkinlandsTerrain(scene: BABYLON.Scene, root: BABYLON.TransformNode, material: BABYLON.Material) {
  const width = 21;
  const depth = 15;
  const columns = 30;
  const rows = 22;
  const positions: number[] = [];
  const indices: number[] = [];
  const uvs: number[] = [];

  for (let row = 0; row <= rows; row += 1) {
    for (let column = 0; column <= columns; column += 1) {
      const x = (column / columns - 0.5) * width;
      const z = (row / rows - 0.5) * depth;
      const bruiseRise = Math.sin(x * 0.82 + z * 0.33) * 0.08 + Math.cos(z * 1.15 - x * 0.17) * 0.05;
      const ridge = Math.max(0, 1 - Math.abs(z + 1.3) / 4.2) * Math.sin(x * 1.7) * 0.05;
      positions.push(x, -1.58 + bruiseRise + ridge, z);
      uvs.push(column / columns, row / rows);
    }
  }

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const base = row * (columns + 1) + column;
      indices.push(base, base + columns + 1, base + 1, base + 1, base + columns + 1, base + columns + 2);
    }
  }

  const normals: number[] = [];
  BABYLON.VertexData.ComputeNormals(positions, indices, normals);
  const vertexData = new BABYLON.VertexData();
  vertexData.positions = positions;
  vertexData.indices = indices;
  vertexData.normals = normals;
  vertexData.uvs = uvs;
  const mesh = new BABYLON.Mesh("skinlands-layered-corpse-terrain", scene);
  vertexData.applyToMesh(mesh);
  mesh.parent = root;
  mesh.material = material;
  return mesh;
}

function createGroundStrip(scene: BABYLON.Scene, root: BABYLON.TransformNode, name: string, x: number, z: number, length: number, width: number, rotation: number, material: BABYLON.Material, y = -1.465) {
  const strip = BABYLON.MeshBuilder.CreateBox(name, { width, height: 0.028, depth: length }, scene);
  strip.parent = root;
  strip.position = new BABYLON.Vector3(x, y, z);
  strip.rotation.y = rotation;
  strip.material = material;
  strip.isPickable = false;
  return strip;
}

function createGroundCylinder(scene: BABYLON.Scene, root: BABYLON.TransformNode, name: string, x: number, z: number, length: number, diameter: number, rotation: number, material: BABYLON.Material, y = -1.39) {
  const cylinder = BABYLON.MeshBuilder.CreateCylinder(name, { height: length, diameterTop: diameter * 0.58, diameterBottom: diameter, tessellation: 6 }, scene);
  cylinder.parent = root;
  cylinder.position = new BABYLON.Vector3(x, y, z);
  cylinder.rotation.z = Math.PI / 2;
  cylinder.rotation.y = rotation;
  cylinder.material = material;
  cylinder.isPickable = false;
  return cylinder;
}

function createBuriedPatch(scene: BABYLON.Scene, root: BABYLON.TransformNode, name: string, x: number, z: number, sx: number, sz: number, rotation: number, material: BABYLON.Material) {
  const patch = BABYLON.MeshBuilder.CreateCylinder(name, { height: 0.03, diameter: 1, tessellation: 7 }, scene);
  patch.parent = root;
  patch.position = new BABYLON.Vector3(x, -1.455, z);
  patch.scaling = new BABYLON.Vector3(sx, 1, sz);
  patch.rotation.y = rotation;
  patch.material = material;
  patch.isPickable = false;
  return patch;
}

function createSkinPlate(scene: BABYLON.Scene, root: BABYLON.TransformNode, x: number, z: number, sx: number, sz: number, rotation: number, material: BABYLON.Material) {
  const plate = BABYLON.MeshBuilder.CreateCylinder("skinlands-raised-corpse-skin-plate", { height: 0.018, diameter: 1, tessellation: 6 }, scene);
  plate.parent = root;
  plate.position = new BABYLON.Vector3(x, -1.447, z);
  plate.scaling = new BABYLON.Vector3(sx, 1, sz);
  plate.rotation.y = rotation;
  plate.material = material;
  plate.isPickable = false;
  return plate;
}

function createRidgeFold(scene: BABYLON.Scene, root: BABYLON.TransformNode, x: number, z: number, length: number, width: number, rotation: number, material: BABYLON.Material) {
  const fold = BABYLON.MeshBuilder.CreateBox("skinlands-dried-skin-ridge-fold", { width, height: 0.045, depth: length }, scene);
  fold.parent = root;
  fold.position = new BABYLON.Vector3(x, -1.395, z);
  fold.rotation.y = rotation;
  fold.material = material;
  fold.isPickable = false;
  return fold;
}

function createJaggedFissure(
  scene: BABYLON.Scene,
  root: BABYLON.TransformNode,
  name: string,
  x: number,
  z: number,
  length: number,
  rotation: number,
  width: number,
  materials: { bloodDark: BABYLON.Material; blood: BABYLON.Material; sinew: BABYLON.Material; bruise: BABYLON.Material }
) {
  const segmentCount = Math.max(5, Math.round(length / 0.52));
  let detailCount = 0;

  for (let index = 0; index < segmentCount; index += 1) {
    const t = segmentCount === 1 ? 0.5 : index / (segmentCount - 1);
    const axial = (t - 0.5) * length;
    const lateral = Math.sin((index + 1) * 1.71) * width * 2.35 + Math.cos(index * 0.83) * width * 0.72;
    const segmentRotation = rotation + Math.sin((index + 1) * 1.19) * 0.26 + (index % 2 === 0 ? 0.06 : -0.05);
    const segmentLength = (length / segmentCount) * (0.74 + (index % 3) * 0.13);
    const segmentWidth = width * (0.78 + (index % 4) * 0.08);
    const sx = x + Math.sin(rotation) * axial + Math.cos(rotation) * lateral;
    const sz = z + Math.cos(rotation) * axial - Math.sin(rotation) * lateral;

    createGroundStrip(scene, root, `${name}-black-underwound`, sx, sz, segmentLength, segmentWidth * 3.1, segmentRotation, materials.bloodDark, -1.462);
    createGroundStrip(scene, root, `${name}-ember-core`, sx, sz, segmentLength * 0.72, segmentWidth, segmentRotation, materials.blood, -1.424);
    detailCount += 2;

    if (index % 2 === 0) {
      const branchRotation = segmentRotation + (index % 4 === 0 ? 0.86 : -0.74);
      const branchX = sx + Math.sin(branchRotation) * segmentLength * 0.2;
      const branchZ = sz + Math.cos(branchRotation) * segmentLength * 0.2;
      createGroundStrip(scene, root, `${name}-dried-sinew-branch`, branchX, branchZ, segmentLength * 0.44, segmentWidth * 0.52, branchRotation, materials.sinew, -1.405);
      detailCount += 1;
    }

    if (index % 3 === 0) {
      createBuriedPatch(scene, root, `${name}-bruised-glow-node`, sx, sz, segmentWidth * 3.2, segmentWidth * 2.1, segmentRotation + 0.31, materials.bruise);
      detailCount += 1;
    }
  }

  return detailCount;
}

function addRuneBoxes(scene: BABYLON.Scene, root: BABYLON.TransformNode, center: BABYLON.Vector3, radius: number, material: BABYLON.Material) {
  const count = 14;
  for (let index = 0; index < count; index += 1) {
    const angle = (index / count) * Math.PI * 2;
    const rune = BABYLON.MeshBuilder.CreateBox("crucible-anvil-platform-rune", { width: 0.045, height: 0.012, depth: 0.16 }, scene);
    rune.parent = root;
    rune.position = new BABYLON.Vector3(center.x + Math.cos(angle) * radius, center.y + 0.09, center.z + Math.sin(angle) * radius);
    rune.rotation.y = -angle;
    rune.material = material;
  }
  return count;
}

function loadGlbUnderNode(scene: BABYLON.Scene, path: string, parent: BABYLON.Node, configure: (container: BABYLON.TransformNode, meshes: BABYLON.AbstractMesh[]) => void) {
  const container = new BABYLON.TransformNode(`stage8-glb-${path.split("/").pop() ?? "asset"}`, scene);
  container.parent = parent;
  container.setEnabled(false);
  const filenameStart = path.lastIndexOf("/") + 1;
  const rootUrl = path.slice(0, filenameStart);
  const sceneFilename = path.slice(filenameStart);

  void BABYLON.SceneLoader.ImportMeshAsync("", rootUrl, sceneFilename, scene).then((result) => {
    const meshes = result.meshes.filter((mesh) => mesh.name !== "__root__");
    for (const mesh of meshes) {
      mesh.parent = container;
      mesh.isPickable = false;
    }
    configure(container, meshes);
    container.setEnabled(true);
  }).catch((error) => {
    console.warn(`Stage 8 GLB failed to load: ${path}`, error);
    container.dispose();
  });
}

function remapImportedStage8Materials(meshes: BABYLON.AbstractMesh[], materials: Record<string, BABYLON.Material>, assetKind: "character" | "weapon") {
  for (const mesh of meshes) {
    const name = mesh.name.toLowerCase();
    if (
      name.includes("crack") ||
      name.includes("fissure") ||
      name.includes("emissive") ||
      name.includes("slit") ||
      name.includes("scar") ||
      (assetKind === "weapon" && (name.includes("pommel") || name.includes("core")))
    ) {
      mesh.material = materials.soulCrack;
    } else if (name.includes("gold") || name.includes("relic") || name.includes("belt") || name.includes("socket")) {
      mesh.material = materials.gold;
    } else if (name.includes("bone") || name.includes("splinter")) {
      mesh.material = materials.bone;
    } else if (name.includes("hood")) {
      mesh.material = materials.importedArmor;
    } else if (name.includes("cloak") || name.includes("tasset")) {
      mesh.material = materials.cloak;
    } else if (name.includes("face") || name.includes("skin")) {
      mesh.material = materials.ashBody;
    } else {
      mesh.material = assetKind === "weapon" ? materials.forgeIron : materials.importedArmor;
    }
    mesh.alwaysSelectAsActiveMesh = true;
  }
}

function addAshBoundSmithCutout(scene: BABYLON.Scene, smith: BABYLON.TransformNode, cutoutPath: string) {
  const texture = new BABYLON.Texture(cutoutPath, scene, false, true, BABYLON.Texture.TRILINEAR_SAMPLINGMODE);
  texture.hasAlpha = true;

  const material = new BABYLON.StandardMaterial("ash-bound-smith-approved-art-cutout-material", scene);
  material.diffuseTexture = texture;
  material.emissiveTexture = texture;
  material.useAlphaFromDiffuseTexture = true;
  material.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND;
  material.disableLighting = true;
  material.alpha = 1;
  material.backFaceCulling = false;

  const cutout = BABYLON.MeshBuilder.CreatePlane("ash-bound-smith-approved-stage8-cutout-billboard", { width: 1.12, height: 1.45 }, scene);
  cutout.parent = smith;
  cutout.position = new BABYLON.Vector3(0.02, 0.9, -0.2);
  cutout.billboardMode = BABYLON.Mesh.BILLBOARDMODE_Y;
  cutout.material = material;
  cutout.isPickable = false;
  cutout.renderingGroupId = 2;
  return cutout;
}

function buildAshBoundSmith(
  scene: BABYLON.Scene,
  root: BABYLON.TransformNode,
  materials: Record<string, BABYLON.Material>,
  runtimeGlbs?: WorldVisualAssets["runtimeGlbs"],
  runtimeVisuals?: WorldVisualAssets["runtimeVisuals"]
) {
  const smith = new BABYLON.TransformNode("ash-bound-smith-gameplay-silhouette", scene);
  smith.parent = root;
  smith.position = new BABYLON.Vector3(-0.15, -1.47, 1.2);
  smith.rotation.y = -0.34;
  smith.scaling.setAll(0.78);

  const legs = [-0.12, 0.12].map((x, index) => {
    const leg = BABYLON.MeshBuilder.CreateCylinder(`ash-bound-smith-obsidian-greave-${index}`, { height: 0.62, diameterTop: 0.11, diameterBottom: 0.14, tessellation: 5 }, scene);
    leg.parent = smith;
    leg.position = new BABYLON.Vector3(x, 0.32, 0);
    leg.material = materials.obsidian;
    return leg;
  });

  const torso = BABYLON.MeshBuilder.CreateBox("ash-bound-smith-obsidian-chest", { width: 0.52, height: 0.7, depth: 0.28 }, scene);
  torso.parent = smith;
  torso.position.y = 0.87;
  torso.material = materials.playerArmor;

  const chestCrack = BABYLON.MeshBuilder.CreateBox("ash-bound-smith-red-soul-crack-chest", { width: 0.045, height: 0.62, depth: 0.018 }, scene);
  chestCrack.parent = smith;
  chestCrack.position = new BABYLON.Vector3(0.02, 0.91, -0.15);
  chestCrack.rotation.z = -0.16;
  chestCrack.material = materials.blood;

  const head = BABYLON.MeshBuilder.CreateSphere("ash-bound-smith-hidden-burned-face", { diameter: 0.28, segments: 6 }, scene);
  head.parent = smith;
  head.position.y = 1.3;
  head.scaling.z = 0.82;
  head.material = materials.ashBody;

  const eye = BABYLON.MeshBuilder.CreateBox("ash-bound-smith-face-ember-eye", { width: 0.14, height: 0.025, depth: 0.018 }, scene);
  eye.parent = smith;
  eye.position = new BABYLON.Vector3(0.04, 1.33, -0.13);
  eye.material = materials.blood;

  const shoulders = [-0.42, 0.42].map((x, index) => {
    const shoulder = BABYLON.MeshBuilder.CreateBox(`ash-bound-smith-asymmetric-shoulder-${index}`, { width: index === 1 ? 0.34 : 0.24, height: 0.16, depth: 0.32 }, scene);
    shoulder.parent = smith;
    shoulder.position = new BABYLON.Vector3(x, 1.1, 0);
    shoulder.rotation.z = index === 1 ? -0.2 : 0.24;
    shoulder.material = materials.obsidian;
    return shoulder;
  });

  const forgeArm = BABYLON.MeshBuilder.CreateCylinder("ash-bound-smith-heavy-forge-arm", { height: 0.66, diameterTop: 0.16, diameterBottom: 0.24, tessellation: 5 }, scene);
  forgeArm.parent = smith;
  forgeArm.position = new BABYLON.Vector3(0.48, 0.79, -0.02);
  forgeArm.rotation.z = -0.34;
  forgeArm.material = materials.forgeIron;

  const offArm = BABYLON.MeshBuilder.CreateCylinder("ash-bound-smith-left-ash-arm", { height: 0.58, diameterTop: 0.1, diameterBottom: 0.14, tessellation: 5 }, scene);
  offArm.parent = smith;
  offArm.position = new BABYLON.Vector3(-0.42, 0.78, 0);
  offArm.rotation.z = 0.28;
  offArm.material = materials.obsidian;

  const cloak = BABYLON.MeshBuilder.CreateBox("ash-bound-smith-torn-cloak-mass", { width: 0.7, height: 1.05, depth: 0.045 }, scene);
  cloak.parent = smith;
  cloak.position = new BABYLON.Vector3(-0.05, 0.53, 0.2);
  cloak.rotation.x = 0.12;
  cloak.material = materials.cloak;

  const cloakStrips: BABYLON.AbstractMesh[] = [];
  for (let index = 0; index < 4; index += 1) {
    const strip = BABYLON.MeshBuilder.CreateBox("ash-bound-smith-torn-cloak-strip", { width: 0.08, height: 0.74 - index * 0.08, depth: 0.035 }, scene);
    strip.parent = smith;
    strip.position = new BABYLON.Vector3(-0.27 + index * 0.17, 0.12, 0.24);
    strip.rotation.z = -0.18 + index * 0.1;
    strip.material = materials.cloak;
    cloakStrips.push(strip);
  }

  const weaponRoot = new BABYLON.TransformNode("crucible-greatblade-root", scene);
  weaponRoot.parent = smith;
  weaponRoot.position = new BABYLON.Vector3(0.55, 0.66, -0.32);
  weaponRoot.rotation.z = -0.84;
  weaponRoot.rotation.y = 0.16;

  const blade = BABYLON.MeshBuilder.CreateBox("crucible-greatblade-black-forged-metal", { width: 0.2, height: 1.48, depth: 0.09 }, scene);
  blade.parent = weaponRoot;
  blade.position.y = 0.38;
  blade.material = materials.forgeIron;

  const edge = BABYLON.MeshBuilder.CreateBox("crucible-greatblade-emissive-soul-crack", { width: 0.045, height: 1.25, depth: 0.105 }, scene);
  edge.parent = weaponRoot;
  edge.position = new BABYLON.Vector3(-0.02, 0.38, -0.015);
  edge.material = materials.blood;

  const socket = BABYLON.MeshBuilder.CreateSphere("crucible-greatblade-essence-socket-core", { diameter: 0.18, segments: 6 }, scene);
  socket.parent = weaponRoot;
  socket.position.y = -0.36;
  socket.material = materials.gold;

  const guard = BABYLON.MeshBuilder.CreateBox("crucible-greatblade-jagged-guard", { width: 0.56, height: 0.08, depth: 0.11 }, scene);
  guard.parent = weaponRoot;
  guard.position.y = -0.48;
  guard.material = materials.obsidian;

  if (runtimeGlbs?.weaponGreatsword) {
    const proxyMeshes = [blade, edge, socket, guard];
    loadGlbUnderNode(scene, runtimeGlbs.weaponGreatsword, weaponRoot, (container, meshes) => {
      remapImportedStage8Materials(meshes, materials, "weapon");
      container.rotation.z = Math.PI / 2;
      container.scaling.setAll(0.76);
      container.position = new BABYLON.Vector3(-0.06, -0.16, 0.02);
      for (const mesh of proxyMeshes) mesh.setEnabled(false);
    });
  }

  if (runtimeGlbs?.ashBoundSmith) {
    const bodyProxyMeshes = [
      ...legs,
      torso,
      chestCrack,
      head,
      eye,
      ...shoulders,
      forgeArm,
      offArm,
      cloak,
      ...cloakStrips
    ];
    loadGlbUnderNode(scene, runtimeGlbs.ashBoundSmith, smith, (container, meshes) => {
      remapImportedStage8Materials(meshes, materials, "character");
      container.scaling.setAll(0.84);
      container.position = new BABYLON.Vector3(0, -0.05, -0.02);
      for (const mesh of bodyProxyMeshes) mesh.setEnabled(false);
    });
  }

  if (runtimeVisuals?.ashBoundSmithCutout) {
    addAshBoundSmithCutout(scene, smith, runtimeVisuals.ashBoundSmithCutout);
  }

  return { smith, parts: legs.length + shoulders.length + 14 };
}

function buildEchoGhost(scene: BABYLON.Scene, root: BABYLON.TransformNode, materials: Record<string, BABYLON.Material>) {
  const echo = new BABYLON.TransformNode("echostate-blue-white-memory-silhouette", scene);
  echo.parent = root;
  echo.position = new BABYLON.Vector3(1.65, -1.43, 1.92);
  echo.rotation.y = 0.62;
  echo.scaling.setAll(0.64);

  const body = BABYLON.MeshBuilder.CreateBox("echostate-ghost-ash-bound-body", { width: 0.34, height: 0.78, depth: 0.16 }, scene);
  body.parent = echo;
  body.position.y = 0.78;
  body.material = materials.echo;

  const head = BABYLON.MeshBuilder.CreateSphere("echostate-ghost-hidden-face", { diameter: 0.22, segments: 6 }, scene);
  head.parent = echo;
  head.position.y = 1.27;
  head.material = materials.echo;

  const weapon = BABYLON.MeshBuilder.CreateBox("echostate-ghost-weapon-outline", { width: 0.06, height: 1.28, depth: 0.035 }, scene);
  weapon.parent = echo;
  weapon.position = new BABYLON.Vector3(0.42, 0.64, -0.1);
  weapon.rotation.z = -0.72;
  weapon.material = materials.echoLine;

  const pathPoints = [
    [-1.8, 2.48],
    [-1.08, 2.28],
    [-0.36, 2.05],
    [0.42, 1.92],
    [1.08, 1.78]
  ];
  for (let index = 0; index < pathPoints.length - 1; index += 1) {
    const [x1, z1] = pathPoints[index];
    const [x2, z2] = pathPoints[index + 1];
    const dx = x2 - x1;
    const dz = z2 - z1;
    const length = Math.hypot(dx, dz);
    const trail = BABYLON.MeshBuilder.CreateBox("echostate-ash-path-trail", { width: 0.035, height: 0.02, depth: length }, scene);
    trail.parent = root;
    trail.position = new BABYLON.Vector3((x1 + x2) / 2, -1.39, (z1 + z2) / 2);
    trail.rotation.y = Math.atan2(dx, dz);
    trail.material = materials.echoLine;
  }

  return { echo, parts: pathPoints.length + 3 };
}

function buildRemnantEnemy(scene: BABYLON.Scene, root: BABYLON.TransformNode, materials: Record<string, BABYLON.Material>, x: number, z: number, rotation: number, scale: number) {
  const enemy = new BABYLON.TransformNode("skinlands-remnant-readable-enemy-silhouette", scene);
  enemy.parent = root;
  enemy.position = new BABYLON.Vector3(x, -1.47, z);
  enemy.rotation.y = rotation;
  enemy.scaling.setAll(scale);

  const body = BABYLON.MeshBuilder.CreateBox("skinlands-remnant-obsidian-body", { width: 0.34, height: 0.54, depth: 0.18 }, scene);
  body.parent = enemy;
  body.position.y = 0.56;
  body.material = materials.obsidian;

  const head = BABYLON.MeshBuilder.CreateSphere("skinlands-remnant-ember-skull", { diameter: 0.19, segments: 5 }, scene);
  head.parent = enemy;
  head.position.y = 0.93;
  head.material = materials.bone;

  const wound = BABYLON.MeshBuilder.CreateBox("skinlands-remnant-red-wound-readability-core", { width: 0.035, height: 0.44, depth: 0.03 }, scene);
  wound.parent = enemy;
  wound.position = new BABYLON.Vector3(0, 0.58, -0.1);
  wound.material = materials.blood;

  const weapon = BABYLON.MeshBuilder.CreateCylinder("skinlands-remnant-jagged-weapon-line", { height: 0.8, diameterTop: 0.035, diameterBottom: 0.06, tessellation: 5 }, scene);
  weapon.parent = enemy;
  weapon.position = new BABYLON.Vector3(0.31, 0.48, -0.06);
  weapon.rotation.z = -0.62;
  weapon.material = materials.forgeIron;

  return enemy;
}

export function buildGameplayWorld(scene: BABYLON.Scene, camera: BABYLON.ArcRotateCamera, visualAssets: WorldVisualAssets) {
  const root = new BABYLON.TransformNode("god-haunted-waste-root", scene);
  const materials = {
    corpseGround: createSkinlandsCorpseTerrainMaterial(scene),
    corpsePlate: standardMaterial(scene, "skinlands-raised-dead-skin-plate-material", "#342523", "#0a0404", 0.26),
    bruise: standardMaterial(scene, "skinlands-bruised-ash-decal-material", "#41272c", "#120506", 0.42),
    sinew: standardMaterial(scene, "skinlands-dried-sinew-root-material", "#1c0b0b", "#070202", 0.34),
    blood: standardMaterial(scene, "skinlands-red-black-fissure-emissive", "#2a0303", "#310303", 0.36),
    bloodDark: standardMaterial(scene, "skinlands-black-fissure-underlay", "#140305", "#050000"),
    obsidian: standardMaterial(scene, "obsidian-carved-iron-material", "#0a0a0f", "#030304"),
    anvilStone: standardMaterial(scene, "crucible-anvil-translucent-proxy-stone", "#080709", "#020202", 0.38),
    forgeIron: standardMaterial(scene, "black-forged-metal-material", "#151315", "#050404"),
    importedArmor: standardMaterial(scene, "ash-bound-imported-obsidian-readable-material", "#1a181a", "#070202"),
    soulCrack: standardMaterial(scene, "ash-bound-bright-soul-crack-emissive-material", "#ff2a16", "#ff1408"),
    gold: standardMaterial(scene, "antique-gold-rune-material", "#b98a25", "#241702", 0.78),
    bone: standardMaterial(scene, "dead-god-bone-ivory-material", "#8b8068", "#2a241d"),
    cloak: standardMaterial(scene, "burned-torn-cloak-material", "#0d0b0d", "#030101"),
    ashBody: standardMaterial(scene, "ash-dark-body-material", "#2f2c2b", "#090909"),
    playerArmor: standardMaterial(scene, "ash-bound-obsidian-armor-material", "#101014", "#060608"),
    echo: standardMaterial(scene, "echostate-blue-white-transparent-memory", "#b9e7ff", "#7fc8ff", 0.24),
    echoLine: standardMaterial(scene, "echostate-blue-white-ash-trail", "#e8f8ff", "#9be0ff", 0.36)
  };
  if (materials.soulCrack instanceof BABYLON.StandardMaterial) {
    materials.soulCrack.disableLighting = true;
    materials.soulCrack.emissiveColor = BABYLON.Color3.FromHexString("#ff2a16");
  }
  const skylineLayer = new BABYLON.Layer(
    "baked-corpse-god-skyline-performance-backdrop",
    visualAssets.skinlandsBackdropPath,
    scene,
    true,
    new BABYLON.Color4(0.92, 0.88, 0.84, 1)
  );
  skylineLayer.isEnabled = false;
  skylineLayer.scale = new BABYLON.Vector2(1, 1);
  skylineLayer.offset = new BABYLON.Vector2(0, 0);

  createSkinlandsTerrain(scene, root, materials.corpseGround);

  let terrainDetailCount = 1;
  terrainDetailCount += 1;

  const skinPlates: [number, number, number, number, number][] = [];
  for (const [x, z, sx, sz, rotation] of skinPlates) {
    createSkinPlate(scene, root, x, z, sx, sz, rotation, materials.corpsePlate);
  }
  terrainDetailCount += skinPlates.length;

  const ridgeFolds = [
    [-5.15, -1.45, 2.6, 0.16, 0.42],
    [-3.0, -3.0, 1.9, 0.13, -0.82],
    [-1.25, 1.05, 2.4, 0.15, 0.06],
    [1.65, -1.95, 2.7, 0.14, -0.38],
    [3.85, 2.25, 2.1, 0.15, 0.72],
    [5.8, -0.5, 2.35, 0.12, -0.16]
  ];
  for (const [x, z, length, width, rotation] of ridgeFolds) {
    createRidgeFold(scene, root, x, z, length, width, rotation, materials.sinew);
  }
  terrainDetailCount += ridgeFolds.length;

  const patches = [
    [-4.8, -2.3, 1.8, 0.72, 0.22],
    [-2.6, 1.8, 1.25, 0.54, -0.38],
    [0.8, -2.7, 1.5, 0.48, 0.71],
    [3.2, 1.6, 1.1, 0.62, -0.2],
    [5.4, -1.2, 1.42, 0.42, 0.56],
    [-6.3, 2.9, 1.15, 0.48, 0.18]
  ];
  for (const [x, z, sx, sz, rotation] of patches) {
    createBuriedPatch(scene, root, "skinlands-bruised-organic-terrain-patch", x, z, sx, sz, rotation, materials.bruise);
  }
  terrainDetailCount += patches.length;

  const fissures: [number, number, number, number, number][] = [];
  for (const [x, z, length, rotation, width] of fissures) {
    terrainDetailCount += createJaggedFissure(scene, root, "skinlands-jagged-red-black-fissure", x, z, length, rotation, width, materials);
  }

  const sinewRoots = [
    [-4.2, 2.8, 1.8, 0.13, -0.4],
    [-3.5, 2.1, 1.35, 0.1, 0.22],
    [-1.8, -2.7, 2.2, 0.13, 0.82],
    [1.6, 2.75, 1.6, 0.11, -0.72],
    [4.6, -2.4, 1.9, 0.12, 0.48],
    [5.7, 0.8, 1.35, 0.1, -0.2],
    [-6.6, -1.9, 1.6, 0.11, 1.08],
    [0.4, 3.45, 1.45, 0.09, 0.12]
  ];
  for (const [x, z, length, diameter, rotation] of sinewRoots) {
    createGroundCylinder(scene, root, "skinlands-dried-sinew-root-prop", x, z, length, diameter, rotation, materials.sinew);
  }
  terrainDetailCount += sinewRoots.length;

  const cystRocks = [
    [-5.5, 0.6, 0.38],
    [-4.6, 3.4, 0.28],
    [-2.1, -3.5, 0.32],
    [1.3, 3.15, 0.34],
    [4.4, 2.9, 0.26],
    [6.2, -0.3, 0.31],
    [3.4, -3.42, 0.36],
    [-6.7, -3.1, 0.28]
  ];
  for (const [x, z, diameter] of cystRocks) {
    const cyst = BABYLON.MeshBuilder.CreateSphere("skinlands-cyst-rock-buried-prop", { diameter, segments: 6 }, scene);
    cyst.parent = root;
    cyst.position = new BABYLON.Vector3(x, -1.34, z);
    cyst.scaling.y = 0.55;
    cyst.material = materials.bruise;
  }
  terrainDetailCount += cystRocks.length;

  const anvilCenter = new BABYLON.Vector3(2.75, -1.36, -0.74);
  const platform = BABYLON.MeshBuilder.CreateCylinder("crucible-anvil-circular-ritual-platform", { height: 0.12, diameter: 1.82, tessellation: 12 }, scene);
  platform.parent = root;
  platform.position = anvilCenter;
  platform.material = materials.anvilStone;
  const ring = BABYLON.MeshBuilder.CreateTorus("crucible-anvil-antique-gold-ritual-ring", { diameter: 1.55, thickness: 0.02, tessellation: 32 }, scene);
  ring.parent = root;
  ring.position = new BABYLON.Vector3(anvilCenter.x, -1.22, anvilCenter.z);
  ring.material = materials.gold;
  terrainDetailCount += 2 + addRuneBoxes(scene, root, anvilCenter, 0.68, materials.blood);

  const anvilPedestal = BABYLON.MeshBuilder.CreateBox("crucible-anvil-obsidian-pedestal", { width: 0.88, height: 0.42, depth: 0.68 }, scene);
  anvilPedestal.parent = root;
  anvilPedestal.position = new BABYLON.Vector3(anvilCenter.x, -0.99, anvilCenter.z);
  anvilPedestal.material = materials.anvilStone;

  const anvilBody = BABYLON.MeshBuilder.CreateBox("crucible-anvil-iron-body-readable-silhouette", { width: 0.86, height: 0.18, depth: 0.36 }, scene);
  anvilBody.parent = root;
  anvilBody.position = new BABYLON.Vector3(anvilCenter.x, -0.56, anvilCenter.z);
  anvilBody.material = materials.anvilStone;

  const anvilHorn = BABYLON.MeshBuilder.CreateCylinder("crucible-anvil-horn-silhouette", { height: 0.34, diameterTop: 0.035, diameterBottom: 0.18, tessellation: 5 }, scene);
  anvilHorn.parent = root;
  anvilHorn.position = new BABYLON.Vector3(anvilCenter.x + 0.54, -0.55, anvilCenter.z);
  anvilHorn.rotation.z = Math.PI / 2;
  anvilHorn.material = materials.anvilStone;

  const anvilCore = BABYLON.MeshBuilder.CreateBox("crucible-anvil-red-forge-core", { width: 0.1, height: 0.06, depth: 0.44 }, scene);
  anvilCore.parent = root;
  anvilCore.position = new BABYLON.Vector3(anvilCenter.x, -0.4, anvilCenter.z);
  anvilCore.material = materials.blood;

  const anvilBeam = BABYLON.MeshBuilder.CreateCylinder("crucible-anvil-vertical-red-forge-beam", { height: 1.9, diameter: 0.028, tessellation: 8 }, scene);
  anvilBeam.parent = root;
  anvilBeam.position = new BABYLON.Vector3(anvilCenter.x, 0.82, anvilCenter.z);
  anvilBeam.material = materials.blood;

  const marker = BABYLON.MeshBuilder.CreateTorus("crucible-anvil-integrated-rune-interaction-marker", { diameter: 0.28, thickness: 0.016, tessellation: 4 }, scene);
  marker.parent = root;
  marker.position = new BABYLON.Vector3(anvilCenter.x, 0.18, anvilCenter.z - 0.22);
  marker.rotation.z = Math.PI / 4;
  marker.material = materials.gold;
  terrainDetailCount += 6;

  const ruinPositions = [
    [-8.1, -0.88, -4.3, 2.2],
    [-6.2, -0.8, 3.7, 1.45],
    [-3.5, -0.78, 5.1, 1.65],
    [4.8, -0.75, 3.45, 1.85],
    [7.4, -0.9, -3.95, 2.45],
    [0.3, -0.83, -5.2, 1.35]
  ];
  for (const [x, y, z, height] of ruinPositions) {
    const spire = BABYLON.MeshBuilder.CreateCylinder("obsidian-broken-spire-silhouette", { height, diameterTop: 0.12, diameterBottom: 0.48, tessellation: 5 }, scene);
    spire.parent = root;
    spire.position = new BABYLON.Vector3(x, y + height / 2, z);
    spire.rotation.z = x > 0 ? 0.18 : -0.14;
    spire.material = materials.obsidian;
  }
  terrainDetailCount += ruinPositions.length;

  for (let group = 0; group < 2; group += 1) {
    for (let index = 0; index < 7; index += 1) {
      const rib = BABYLON.MeshBuilder.CreateCylinder("dead-god-rib-emerging-from-skinlands", { height: 2.2 - index * 0.12, diameterTop: 0.08, diameterBottom: 0.17, tessellation: 5 }, scene);
      rib.parent = root;
      rib.position = new BABYLON.Vector3(-4.4 + group * 7.8 + index * 0.32, -0.68, -4.1 + group * 0.5 - index * 0.06);
      rib.rotation.z = -0.82 + index * 0.13;
      rib.rotation.x = 0.18;
      rib.material = materials.bone;
    }
  }
  terrainDetailCount += 14;

  const brazierPositions = [[1.35, -1.9], [4.15, 0.55], [1.35, 0.46], [4.1, -1.92], [-1.75, 2.2]] as const;
  for (const [x, z] of brazierPositions) {
    const brazier = BABYLON.MeshBuilder.CreateCylinder("crucible-red-brazier-ember-prop", { height: 0.26, diameterTop: 0.07, diameterBottom: 0.12, tessellation: 5 }, scene);
    brazier.parent = root;
    brazier.position = new BABYLON.Vector3(x, -1.29, z);
    brazier.material = materials.obsidian;
    const flame = BABYLON.MeshBuilder.CreateSphere("crucible-brazier-red-flame-core", { diameter: 0.07, segments: 6 }, scene);
    flame.parent = root;
    flame.position = new BABYLON.Vector3(x, -1.11, z);
    flame.material = materials.soulCrack;
  }
  terrainDetailCount += brazierPositions.length * 2;

  const player = buildAshBoundSmith(scene, root, materials, visualAssets.runtimeGlbs, visualAssets.runtimeVisuals);
  const echo = buildEchoGhost(scene, root, materials);
  const enemies = [
    buildRemnantEnemy(scene, root, materials, -2.05, -0.35, -0.22, 0.92),
    buildRemnantEnemy(scene, root, materials, -1.22, 2.28, 0.5, 0.8),
    buildRemnantEnemy(scene, root, materials, 3.92, 1.0, -0.65, 0.86)
  ];
  terrainDetailCount += player.parts + echo.parts + enemies.length * 4;

  const ashParticles = new BABYLON.ParticleSystem("skinlands-ash-blood-particle-cap", 360, scene);
  ashParticles.particleTexture = new BABYLON.Texture("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+/p94AAAAASUVORK5CYII=", scene);
  ashParticles.emitter = new BABYLON.Vector3(0, -0.55, 0);
  ashParticles.minEmitBox = new BABYLON.Vector3(-8.5, -0.25, -5.8);
  ashParticles.maxEmitBox = new BABYLON.Vector3(8.5, 1.8, 5.8);
  ashParticles.color1 = new BABYLON.Color4(0.48, 0.43, 0.39, 0.78);
  ashParticles.color2 = new BABYLON.Color4(0.56, 0.02, 0.02, 0.74);
  ashParticles.minSize = 0.018;
  ashParticles.maxSize = 0.055;
  ashParticles.emitRate = 58;
  ashParticles.minLifeTime = 1.4;
  ashParticles.maxLifeTime = 3.8;
  ashParticles.gravity = new BABYLON.Vector3(0.02, 0.016, -0.01);
  ashParticles.stop();

  const worldLight = new BABYLON.HemisphericLight("skinlands-ash-sky-cinematic-fill", new BABYLON.Vector3(-0.4, 1, -0.25), scene);
  worldLight.parent = root;
  worldLight.diffuse = BABYLON.Color3.FromHexString("#7a5a50");
  worldLight.groundColor = BABYLON.Color3.FromHexString("#3a1815");
  worldLight.intensity = 1.04;

  const forgeLight = new BABYLON.PointLight("crucible-anvil-red-gold-core-light", new BABYLON.Vector3(anvilCenter.x, 0.25, anvilCenter.z), scene);
  forgeLight.parent = root;
  forgeLight.diffuse = BABYLON.Color3.FromHexString("#8b0000");
  forgeLight.intensity = 3.8;
  forgeLight.range = 6.2;

  const playerLight = new BABYLON.PointLight("ash-bound-smith-soul-crack-fill-light", new BABYLON.Vector3(-0.2, -0.15, 1.0), scene);
  playerLight.parent = root;
  playerLight.diffuse = BABYLON.Color3.FromHexString("#d4af37");
  playerLight.intensity = 0.9;
  playerLight.range = 3.2;

  const worldState: WorldAuditState = {
    active: false,
    biome: "Skinlands",
    cameraViewDegrees: 52,
    playerVisible: true,
    forgedWeaponVisible: true,
    echoGhostVisible: true,
    anvilLandmarkVisible: true,
    readableEnemyCount: enemies.length,
    terrainDetailCount,
    materialLayerCount: 8,
    particleBudget: 360,
    qualityGateReady: false
  };

  const publishWorldState = () => {
    (window as Window & { __soulsmithWorldState?: WorldAuditState }).__soulsmithWorldState = worldState;
  };
  publishWorldState();
  root.setEnabled(false);

  const activate = () => {
    root.setEnabled(true);
    skylineLayer.isEnabled = true;
    ashParticles.start();
    scene.clearColor = new BABYLON.Color4(0.032, 0.026, 0.028, 1);
    scene.fogMode = BABYLON.Scene.FOGMODE_EXP2;
    scene.fogColor = BABYLON.Color3.FromHexString("#322121");
    scene.fogDensity = 0.018;
    camera.alpha = -Math.PI / 2.38;
    camera.beta = BABYLON.Tools.ToRadians(52);
    camera.radius = 10.1;
    camera.setTarget(new BABYLON.Vector3(0.62, -0.64, 0.36));
    camera.lowerRadiusLimit = 7.2;
    camera.upperRadiusLimit = 12.5;
    worldState.active = true;
    worldState.qualityGateReady = worldState.playerVisible && worldState.forgedWeaponVisible && worldState.echoGhostVisible && worldState.anvilLandmarkVisible && worldState.readableEnemyCount >= 3 && worldState.terrainDetailCount >= 90 && worldState.materialLayerCount >= 6;
    publishWorldState();
  };

  return { root, activate };
}
